import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { setLoading, setSnackInfo } from '../../redux/actions/appAction'
import HttpService from '../../services/httpService'
import {
    Paper, Button, Table, TableBody, TableCell, TableContainer, TableRow, TableHead, TablePagination,
    TextField, Chip, Tooltip, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    MenuItem, Select, FormControl, InputLabel, Box, Typography
} from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import HistoryIcon from '@material-ui/icons/History'
import SearchIcon from '@material-ui/icons/Search'
import ClearAllIcon from '@material-ui/icons/ClearAll'
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined'
import CloseIcon from '@material-ui/icons/Close'
import moment from 'moment'

// same orange palette as the Lost & Refund Approval Bypass screen
const ORANGE = '#F47E44'
const ORANGE_DARK = '#c1531f'
const ORANGE_LIGHT = '#fff1ea'
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif"
const DATE_TIME = 'DD-MM-YYYY hh:mm A'

const STATUS_STYLES = {
    Pending: { backgroundColor: '#fff3e0', color: '#ef6c00' },
    Approved: { backgroundColor: '#eafaf1', color: '#1e8449' },
    Rejected: { backgroundColor: '#fdecea', color: '#c0392b' },
    Revoked: { backgroundColor: '#eceff1', color: '#546e7a' },
}

const outlinedField = {
    '& .MuiOutlinedInput-root': { borderRadius: 12, backgroundColor: '#fff' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${ORANGE}88` },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
}

const OrangeTabs = withStyles({
    indicator: { backgroundColor: ORANGE, height: 3 },
})(Tabs)

const OrangeTab = withStyles({
    root: { textTransform: 'none', fontWeight: 600, '&.Mui-selected': { color: ORANGE_DARK } },
})(Tab)

// the full value of the chosen type is matched
const SEARCH_TYPES = [
    { label: 'Unique Lead No', by: 'uniqueLeadNumber' },
    { label: 'Project No', by: 'projectNo' },
    { label: 'Lead No', by: 'leadNo' },
]

const TABS = ['All', 'Pending', 'Approved', 'Rejected', 'Revoked']

const useStyles = makeStyles(() => ({
    page: { padding: 20, background: '#f6f7fb', minHeight: '100vh' },
    headerCard: {
        marginBottom: 20, borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 28px rgba(196,83,31,0.2)',
        background: HEADER_GRADIENT, padding: '22px 26px', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 16,
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
    headerIconWrap: {
        width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,255,255,0.22)',
        border: '2px solid rgba(255,255,255,0.35)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    },
    statChip: {
        background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 20, padding: '7px 16px',
        fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
    },
    filterCard: { borderRadius: 16, boxShadow: '0 6px 20px rgba(17,24,39,0.06)', marginBottom: 20, background: '#fff', overflow: 'hidden' },
    sectionLabel: {
        display: 'flex', alignItems: 'center', gap: 9, color: '#6b7280', fontSize: 12.5, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 12,
    },
    iconBadge: {
        width: 28, height: 28, borderRadius: 9, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, color: '#fff', background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        boxShadow: '0 3px 9px rgba(244,126,68,0.38)',
    },
    filterRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 },
    select: { width: 210, flex: '0 0 auto', ...outlinedField },
    searchField: { flex: '1 1 260px', maxWidth: 420, ...outlinedField },
    submitBtn: {
        borderRadius: 10, textTransform: 'none', fontWeight: 600, color: '#fff',
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`, boxShadow: 'none', padding: '8px 20px',
        '&:hover': { background: `linear-gradient(135deg, #a8441a, ${ORANGE_DARK})`, boxShadow: '0 6px 16px rgba(244,126,68,0.35)' },
    },
    clearBtn: {
        borderRadius: 10, textTransform: 'none', fontWeight: 600, color: '#6b7280', border: '1.5px solid #d8dee8',
        background: '#fff', padding: '8px 20px', '&:hover': { background: '#f5f6f8', borderColor: '#c3cad6' },
    },
    tableCard: { borderRadius: 16, overflow: 'hidden', boxShadow: '0 6px 20px rgba(17,24,39,0.06)', background: '#fff' },
    // fixed layout: every column gets a share of the width, so the whole table fits the screen
    table: { tableLayout: 'fixed', width: '100%' },
    headCell: {
        background: `${ORANGE_DARK} !important`, color: '#fff', fontWeight: 700, fontSize: 11,
        letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'center', borderBottom: 'none', padding: '10px 6px',
    },
    row: {
        '&:nth-of-type(odd)': { backgroundColor: '#FBFBFA' },
        '&:hover': { backgroundColor: ORANGE_LIGHT, boxShadow: `inset 3px 0 0 ${ORANGE}` },
        transition: 'background-color .12s ease, box-shadow .12s ease',
    },
    cell: { padding: '10px 6px', textAlign: 'center', fontSize: 13, color: '#2b3648', wordBreak: 'break-word', verticalAlign: 'middle', borderBottom: '1px solid #f1f2f5' },
    leadLink: {
        display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 7, background: ORANGE_LIGHT,
        color: ORANGE_DARK, fontWeight: 700, fontSize: 12.5, textDecoration: 'none', wordBreak: 'break-all',
        '&:hover': { background: '#ffe3d2', textDecoration: 'none' },
    },
    dialogTitle: { fontSize: 18, fontWeight: 700, color: ORANGE_DARK, fontFamily: HEADING_FONT },
    detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16, marginBottom: 18 },
    detailBlock: { marginBottom: 18 },
    detailLabel: { fontSize: 12, color: '#888', marginBottom: 4 },
    step: { display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 },
    dot: { width: 12, height: 12, borderRadius: '50%', marginTop: 4, flexShrink: 0 },
}))

const DetailItem = ({ label, value }) => (
    <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 600, wordBreak: 'break-word' }}>{value || '-'}</div>
    </div>
)

// opens the lead in its project log page, in a new tab
const LeadLink = ({ lead, classes }) => lead?._id
    ? <Link to={`/project-logs/${lead._id}`} target="_blank" rel="noopener noreferrer" className={classes.leadLink}>{lead.uniqueLeadNumber || lead.lead_no || 'Open'}</Link>
    : <span>-</span>

const TwoLineDate = ({ date }) => date ? (
    <>
        <div>{moment(date).format('DD-MM-YYYY')}</div>
        <div style={{ color: '#888', fontSize: 12 }}>{moment(date).format('hh:mm A')}</div>
    </>
) : <span style={{ color: '#bbb' }}>—</span>

const DiscountApprovalHistory = (props) => {
    const classes = useStyles()

    const [status, setStatus] = useState('All')
    const [rows, setRows] = useState([])
    const [totalItems, setTotalItems] = useState(0)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [counts, setCounts] = useState({ Pending: 0, Approved: 0, Rejected: 0, Revoked: 0 })
    const [viewAll, setViewAll] = useState(false)
    const [trackedAbove, setTrackedAbove] = useState(13)

    const [searchType, setSearchType] = useState(SEARCH_TYPES[0].label)
    const [searchValue, setSearchValue] = useState('')
    const [appliedSearch, setAppliedSearch] = useState(null) // { by, value } once Search is pressed

    const [detail, setDetail] = useState(null)

    const load = useCallback(async () => {
        props.setLoading(true)
        try {
            const res = await HttpService.getDiscountApprovalHistory({
                status: status === 'All' ? undefined : status,
                by: appliedSearch?.by,
                value: appliedSearch?.value,
                page: page + 1,
                limit: rowsPerPage,
            })
            setRows(res.data.data || [])
            setTotalItems(res.data.totalItems || 0)
            setCounts(res.data.statusCounts || { Pending: 0, Approved: 0, Rejected: 0, Revoked: 0 })
            setViewAll(!!res.data.viewAll)
            setTrackedAbove(res.data.trackedAbove ?? 13)
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Could not load the discount requests', 'error')
        }
        props.setLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, appliedSearch, page, rowsPerPage])

    useEffect(() => { load() }, [load])

    const handleSearch = () => {
        if (!searchValue.trim()) return props.setSnackInfo(`Please enter the ${searchType} to search`, 'warning')
        setPage(0)
        setAppliedSearch({ by: SEARCH_TYPES.find(t => t.label === searchType).by, value: searchValue.trim() })
    }

    const handleClear = () => {
        setSearchValue('')
        setPage(0)
        setAppliedSearch(null)
    }

    const tabLabel = (name) => {
        const total = name === 'All' ? Object.values(counts).reduce((a, b) => a + b, 0) : counts[name]
        return `${name} (${total || 0})`
    }

    // the two (or three) steps a request goes through, for the details popup
    const stepsOf = (r) => {
        const steps = [{
            color: ORANGE_DARK,
            title: `Raised by ${r.requestedBy?.name || '-'}`,
            when: r.createdAt,
            text: `${r.previousPercent}% → ${r.requestedPercent}% raised for approval`,
        }]
        if (r.status === 'Pending') {
            steps.push({ color: '#ef6c00', title: 'Waiting for approval', text: 'Nobody has approved or rejected it yet' })
        } else {
            const verb = r.status === 'Revoked' ? 'Revoked' : r.status
            steps.push({
                color: STATUS_STYLES[r.status].color,
                title: `${verb} by ${r.actionTakenBy?.name || '-'}`,
                when: r.actionTakenAt,
                text: r.status === 'Revoked' ? 'The request was taken back by the person who raised it' : `${verb} at ${r.decidedPercent ?? r.requestedPercent}%`,
            })
        }
        return steps
    }

    return (
        <div className={classes.page}>
            <Box className={classes.headerCard}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.headerIconWrap}><HistoryIcon style={{ color: '#fff', fontSize: 26 }} /></Box>
                    <Box>
                        <Typography style={{ color: '#fff', fontFamily: HEADING_FONT, fontSize: 21, margin: 0 }}>
                            Discount Approval History
                        </Typography>
                        <Typography style={{ color: 'rgba(255,255,255,0.88)', fontSize: 13.5, marginTop: 4 }}>
                            Discount requests above {trackedAbove}% raised for approval: who raised them, when, and who approved or rejected them
                            {!viewAll && ' (showing the requests raised by you)'}
                        </Typography>
                    </Box>
                </Box>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Box className={classes.statChip}>{counts.Pending || 0} Pending</Box>
                    <Box className={classes.statChip}>{counts.Approved || 0} Approved</Box>
                    <Box className={classes.statChip}>{counts.Rejected || 0} Rejected</Box>
                </Box>
            </Box>

            <Box className={classes.filterCard}>
                <Box style={{ padding: '18px 22px' }}>
                    <Typography className={classes.sectionLabel}>
                        <span className={classes.iconBadge}><SearchIcon style={{ fontSize: 15 }} /></span>
                        Search
                    </Typography>
                    <Box className={classes.filterRow}>
                        <FormControl variant="outlined" size="small" className={classes.select}>
                            <InputLabel>Search By</InputLabel>
                            <Select value={searchType} onChange={(e) => { setSearchType(e.target.value); setSearchValue('') }} label="Search By">
                                {SEARCH_TYPES.map(option => <MenuItem key={option.by} value={option.label}>{option.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            variant="outlined"
                            label={`Enter ${searchType}`}
                            className={classes.searchField}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
                        />
                        <Button variant="contained" className={classes.submitBtn} startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button>
                        <Button variant="outlined" className={classes.clearBtn} startIcon={<ClearAllIcon />} onClick={handleClear}>Clear</Button>
                    </Box>
                </Box>
            </Box>

            <Paper className={classes.tableCard}>
                <OrangeTabs value={status} onChange={(e, value) => { setStatus(value); setPage(0) }}>
                    {TABS.map(name => <OrangeTab key={name} value={name} label={tabLabel(name)} />)}
                </OrangeTabs>

                <TableContainer style={{ overflowX: 'hidden' }}>
                    <Table size="small" className={classes.table}>
                        <colgroup>
                            <col style={{ width: '4%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '9%' }} />
                            <col style={{ width: '7%' }} />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.headCell}>Sr No.</TableCell>
                                <TableCell className={classes.headCell}>Unique Lead No</TableCell>
                                <TableCell className={classes.headCell}>Project No</TableCell>
                                <TableCell className={classes.headCell}>Customer</TableCell>
                                <TableCell className={classes.headCell}>Raised Date</TableCell>
                                <TableCell className={classes.headCell}>Raised By</TableCell>
                                <TableCell className={classes.headCell}>Discount %</TableCell>
                                <TableCell className={classes.headCell}>Status</TableCell>
                                <TableCell className={classes.headCell}>Approved / Rejected By</TableCell>
                                <TableCell className={classes.headCell}>Approved / Rejected Date</TableCell>
                                <TableCell className={classes.headCell}>View</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r, index) => (
                                <TableRow key={r._id} className={classes.row}>
                                    <TableCell className={classes.cell}>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell className={classes.cell}><LeadLink lead={r.leadId} classes={classes} /></TableCell>
                                    <TableCell className={classes.cell}>{r.leadId?.erpProjectNo || '-'}</TableCell>
                                    <TableCell className={classes.cell}>{r.leadId?.customerId?.name || '-'}</TableCell>
                                    <TableCell className={classes.cell}><TwoLineDate date={r.createdAt} /></TableCell>
                                    <TableCell className={classes.cell}>{r.requestedBy?.name || '-'}</TableCell>
                                    <TableCell className={classes.cell}>{r.previousPercent} → <b>{r.requestedPercent}</b></TableCell>
                                    <TableCell className={classes.cell}><Chip size="small" label={r.status} style={STATUS_STYLES[r.status]} /></TableCell>
                                    <TableCell className={classes.cell}>{r.status === 'Pending' ? <span style={{ color: '#bbb' }}>—</span> : (r.actionTakenBy?.name || '-')}</TableCell>
                                    <TableCell className={classes.cell}><TwoLineDate date={r.actionTakenAt} /></TableCell>
                                    <TableCell className={classes.cell} align="center">
                                        <Tooltip title="View details">
                                            <IconButton size="small" onClick={() => setDetail(r)}>
                                                <VisibilityOutlinedIcon fontSize="small" style={{ color: ORANGE_DARK }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={11} align="center" style={{ color: '#888', padding: 28 }}>No discount requests found</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalItems}
                    page={page}
                    onChangePage={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Paper>

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
                <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center' }}>
                    <span className={classes.dialogTitle} style={{ flex: 1 }}>Discount request details</span>
                    {detail && <Chip size="small" label={detail.status} style={STATUS_STYLES[detail.status]} />}
                    <IconButton size="small" onClick={() => setDetail(null)} style={{ marginLeft: 8 }}><CloseIcon /></IconButton>
                </DialogTitle>
                {detail && (
                    <DialogContent dividers>
                        <div className={classes.detailGrid}>
                            <DetailItem label="Unique Lead No" value={<LeadLink lead={detail.leadId} classes={classes} />} />
                            <DetailItem label="Lead No" value={detail.leadId?.lead_no} />
                            <DetailItem label="Project No" value={detail.leadId?.erpProjectNo} />
                            <DetailItem label="Customer" value={detail.leadId?.customerId?.name} />
                            <DetailItem label="Experience Center" value={detail.leadId?.experienceCenterId?.name} />
                            <DetailItem label="Stage" value={detail.leadId?.salesStage} />
                            <DetailItem label="Previous discount" value={`${detail.previousPercent}%`} />
                            <DetailItem label="Requested discount" value={`${detail.requestedPercent}%`} />
                            <DetailItem label="Raised by" value={detail.requestedBy ? `${detail.requestedBy.name}${detail.requestedBy.email ? ` (${detail.requestedBy.email})` : ''}` : '-'} />
                        </div>

                        <div className={classes.detailBlock}>
                            <div className={classes.detailLabel}>Timeline</div>
                            {stepsOf(detail).map((step, i) => (
                                <div key={i} className={classes.step}>
                                    <span className={classes.dot} style={{ backgroundColor: step.color }} />
                                    <div>
                                        <div style={{ fontSize: 14, fontWeight: 600 }}>{step.title}</div>
                                        {step.when && <div style={{ fontSize: 12, color: '#888' }}>{moment(step.when).format(DATE_TIME)}</div>}
                                        <div style={{ fontSize: 13, color: '#555' }}>{step.text}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setDetail(null)} style={{ textTransform: 'none', color: ORANGE_DARK, fontWeight: 600 }}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default connect(null, { setLoading, setSnackInfo })(DiscountApprovalHistory)
